import { FlexRowCenterVertical, StyleUtils } from '@main/core-ui';
import { Validator } from 'jsonschema';
import React, { useRef } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '../../Button';
import type { CalloutProps } from '../../Callout';
import { Callout } from '../../Callout';
import { CalloutLevel } from '../../Callout/wrappers';
import { CODE_FONT_STYLE } from '../../CodeBlock/CodeBlock';
import { CodeEditor, type CodeEditorRef } from '../../CodeEditor';
import { TEST_RUN_EMPTY_STATE } from '../defaults';
import { enableCustomFunctionJsonMode } from '../editor-config';
import { getCustomFunctionJsonSchema } from '../editor-config/helpers/getCustomFunctionJsonSchema';
import { getDefaultTestString } from '../helpers';
import { customFunctionMessages } from '../messages';
import {
  CustomFunctionActionType,
  type CustomFunctionStateAction,
} from '../state';
import type { EnvironmentVariable, TestRun, TestRunHandler } from '../types';
import { DescriptionWrapper } from '../wrappers';
import { Environment } from './shared';

// JSON schema validator
const v = new Validator();

// Logger
const logger = console;

// The name of the JSON file, used by the editor
const JSON_FILENAME = 'test-parameters.json';

interface TestingTabProps {
  /** The code string */
  value: string;
  /** The dispatch function to handle changes to the code string and test run */
  dispatch: React.Dispatch<CustomFunctionStateAction>;
  /** The env vars set in the Environment Variables tab */
  envVars: EnvironmentVariable[];
  /** The test run metadata */
  testRun: TestRun;
  /** The handler for running a test */
  handleTestRun: TestRunHandler;
}

export const TestingTab: React.FC<TestingTabProps> = ({
  value,
  dispatch,
  envVars,
  testRun,
  handleTestRun,
}) => {
  const { formatMessage } = useIntl();
  const editorRef = useRef<CodeEditorRef>(null);

  return (
    <>
      <DescriptionWrapper>
        {formatMessage(customFunctionMessages.testingInstructions, {
          Environment,
        })}
      </DescriptionWrapper>
      <div style={{ flexGrow: 1, minHeight: '50px' }}>
        <CodeEditor
          ref={editorRef}
          height="100%"
          language="json"
          filename={JSON_FILENAME}
          defaultValue={value}
          onChange={(newCode) =>
            dispatch({
              type: CustomFunctionActionType.UPDATE_TEST_JSON,
              testJson: newCode,
            })
          }
          onMount={(editor, monaco) => {
            enableCustomFunctionJsonMode(monaco, JSON_FILENAME, envVars);
          }}
        />
      </div>
      <FlexRowCenterVertical
        style={{ justifyContent: 'flex-start', gap: StyleUtils.Spacing.sm }}
      >
        <Button
          icon="lightning"
          variant="primary"
          size="sm"
          onClick={async () => {
            // Reset the test run state
            dispatch({ type: CustomFunctionActionType.START_TEST });

            // Since we're in the function's execution scope, we can't use the component's updated `testRun` state, so we represent it separately.
            const testRunStartState: TestRun = {
              ...TEST_RUN_EMPTY_STATE,
              status: 'pending',
            };

            try {
              // Attempt to parse the test payload as JSON
              let testJsonParsed;
              try {
                testJsonParsed = JSON.parse(value);
              } catch (error) {
                dispatch({
                  type: CustomFunctionActionType.UPDATE_TEST_RUN,
                  testRun: {
                    ...testRunStartState,
                    status: 'validationError',
                    logs: [
                      {
                        header: formatMessage(
                          customFunctionMessages.testRunLogsHeaderGeneralError,
                        ),
                        message:
                          `Invalid test payload. Failed to parse the test payload as JSON. ` +
                          `Check the test payload above for highlighted warnings.` +
                          `\n${error.toString()}`,
                      },
                    ],
                  },
                });
                return;
              }

              // Validate the test payload against the custom function JSON schema
              const result = v.validate(
                testJsonParsed,
                getCustomFunctionJsonSchema([]),
                { nestedErrors: true },
              );

              // Give fast feedback if the test payload is invalid
              if (!result.valid || result.errors.length > 0) {
                dispatch({
                  type: CustomFunctionActionType.UPDATE_TEST_RUN,
                  testRun: {
                    ...testRunStartState,
                    status: 'validationError',
                    logs: [
                      {
                        header: formatMessage(
                          customFunctionMessages.testRunLogsHeaderGeneralError,
                        ),
                        message:
                          `Invalid test payload schema. Check the test payload above for highlighted warnings.` +
                          `\n${result.toString()}`,
                      },
                    ],
                  },
                });
                return;
              }

              // Run the custom function with the test payload and environment variables
              const { data, errors } = await handleTestRun(
                testJsonParsed.payload,
                testJsonParsed.environment,
              );

              // Any server errors fall through to the catch block
              if (!data || errors) {
                throw new Error(`${errors?.map((e) => e.message).join('\n')}`);
              }

              // Set the test status
              const status =
                data.result.error || data.result.exitCode > 0
                  ? 'functionError'
                  : 'success';

              // Track log streams by actor (the Custom Function, Sombra, and everything else)
              const upstreamLogs: CalloutProps['upstreamLogs'] = [];

              // Parse logs from the custom function
              const customFunctionLogs =
                data.result.logs.map(({ message }) => message).join('\n') ?? '';

              // Logs from the Custom Function
              upstreamLogs.push({
                header: formatMessage(
                  customFunctionMessages.testRunLogsHeaderCustomFunction,
                ),
                message: customFunctionLogs,
              });

              // Logs from the Sombra service
              if (data.result.error) {
                upstreamLogs.push({
                  header: formatMessage(
                    customFunctionMessages.testRunLogsHeaderSombraError,
                  ),
                  message: data.result.error.message,
                });
              }

              dispatch({
                type: CustomFunctionActionType.UPDATE_TEST_RUN,
                testRun: {
                  ...testRunStartState,
                  timeMs: data.result.profile.timeMs,
                  logs: upstreamLogs,
                  status,
                  exitCode: data.result.exitCode,
                },
              });
            } catch (error) {
              logger.error(error);
              dispatch({
                type: CustomFunctionActionType.UPDATE_TEST_RUN,
                testRun: {
                  ...testRunStartState,
                  logs: [
                    {
                      header: formatMessage(
                        customFunctionMessages.testRunLogsHeaderGeneralError,
                      ),
                      message: `An unexpected error occurred while running the test.\n${error.message}`,
                    },
                  ],
                  status: 'serverError',
                },
              });
            }
          }}
        >
          {formatMessage(customFunctionMessages.sendTestRequest)}
        </Button>
        <Button
          icon="arrow-rotate"
          variant="outline-secondary"
          size="sm"
          onClick={() => {
            // Force a new value for the editor
            editorRef.current?.setValue(getDefaultTestString(envVars));
          }}
        >
          {formatMessage(customFunctionMessages.resetTestPayload)}
        </Button>
      </FlexRowCenterVertical>

      <TestRunFeedback testRun={testRun} dispatch={dispatch} />
    </>
  );
};

const TestRunFeedback: React.FC<{
  /** The test run */
  testRun: TestRun;
  /** The dispatch function to handle changes to the code string and test run */
  dispatch: React.Dispatch<CustomFunctionStateAction>;
}> = ({ testRun, dispatch }) => {
  const { formatMessage, formatNumber } = useIntl();

  if (testRun.status === 'idle') {
    return null;
  }

  const title = (
    {
      success: formatMessage(customFunctionMessages.testRunSuccessTitle),
      serverError: formatMessage(customFunctionMessages.testRunErrorTitle),
      functionError: formatMessage(customFunctionMessages.testRunErrorTitle),
      validationError: formatMessage(
        customFunctionMessages.testRunValidationErrorTitle,
      ),
      pending: formatMessage(customFunctionMessages.testRunPendingTitle),
    } as Record<TestRun['status'], string>
  )[testRun.status];

  const message = (
    {
      success: formatMessage(customFunctionMessages.testRunSuccessMessage),
      serverError: formatMessage(
        customFunctionMessages.testRunServerErrorMessage,
      ),
      functionError: formatMessage(
        customFunctionMessages.testRunFunctionErrorMessage,
      ),
      validationError: formatMessage(
        customFunctionMessages.testRunValidationErrorMessage,
      ),
      pending: formatMessage(customFunctionMessages.testRunPendingMessage),
    } as Record<TestRun['status'], string>
  )[testRun.status];

  const level: CalloutLevel = (
    {
      success: 'success',
      serverError: 'error',
      functionError: 'error',
      validationError: 'warning',
      pending: 'info',
    } as Record<TestRun['status'], CalloutLevel>
  )[testRun.status];

  const testIsComplete = !['idle', 'pending'].includes(testRun.status);

  // TODO: T-44443 - Refactor formattedDuration pattern into a helper with detailed usage instructions
  // Format the test run duration
  const ms = testRun.timeMs ?? 0;
  const isSeconds = ms >= 1000;
  const unit = isSeconds ? 'second' : 'millisecond';
  const value = isSeconds ? ms / 1000 : ms;
  const formattedDuration = formatNumber(value, {
    style: 'unit',
    unit,
    unitDisplay: 'narrow',
    minimumSignificantDigits: 3,
    maximumSignificantDigits: 3,
  });

  return (
    <Callout
      title={title}
      level={level}
      message={message}
      dismissable={
        testIsComplete && {
          onDismiss: () =>
            dispatch({ type: CustomFunctionActionType.CLEAR_TEST_RUN }),
        }
      }
      upstreamLogs={testRun.logs}
      footer={
        testIsComplete &&
        testRun.timeMs !== undefined && (
          <div
            style={{ ...CODE_FONT_STYLE, fontSize: '0.75em', opacity: 0.75 }}
          >
            {testRun.exitCode ? (
              <div>
                {formatMessage(customFunctionMessages.exitCode, {
                  exitCode: testRun.exitCode,
                  duration: formattedDuration,
                })}
              </div>
            ) : (
              <div>
                {formatMessage(customFunctionMessages.functionExecutedIn, {
                  duration: formattedDuration,
                })}
              </div>
            )}
          </div>
        )
      }
    />
  );
};
